<template>
  <v-card title="Products">
    <template v-slot:append>
      <v-icon icon="mdi-plus" @click="openModal('productAdd')"></v-icon>
      <v-tooltip activator="parent" location="end">Create Product</v-tooltip>
    </template>
    <v-card-text>
      <v-expansion-panels v-for="(product, index) in products" :key="index">
        <v-expansion-panel>
          <v-expansion-panel-title>
            {{ product.title }}
            <v-icon icon="mdi-pencil" @click="selectProduct(product)">
              <v-tooltip activator="parent" location="end">Edit Product</v-tooltip>
            </v-icon>
            <v-icon icon="mdi-trash-can-outline" @click="removeProductModal(product)">
              <v-tooltip activator="parent" location="end">Delete Product</v-tooltip>
            </v-icon></v-expansion-panel-title
          >
          <v-expansion-panel-text>
            <v-expansion-panels v-for="(variant, vIndex) in product.variants" :key="vIndex">
              <v-expansion-panel>
                <v-expansion-panel-title v-html="variant.description"></v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-table>
                    <tbody>
                      <tr>
                        <td><strong>Price:</strong></td>
                        <td>
                          <div>${{ variant.price }}</div>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Start Date:</strong></td>
                        <td>
                          <div>{{ variant.displayStartDate }}</div>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>End Date:</strong></td>
                        <td>
                          <div>{{ variant.displayEndDate }}</div>
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                  <v-expansion-panels
                    v-for="(addition, aIndex) in variant.productAdditions"
                    :key="aIndex">
                    <v-expansion-panel>
                      <v-expansion-panel-title>{{ addition.name }}</v-expansion-panel-title>
                      <v-expansion-panel-text>{{ addition.description }}</v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
  <ConfirmDeleteComponent
    item="Product"
    :description="selectedProduct.title"
    :isOpen="isRemoveModalOpen"
    @actionCanceled="isRemoveModalOpen = !isRemoveModalOpen"
    @actionConfirmed="handleRemoveProduct"></ConfirmDeleteComponent>

  <v-dialog v-model="addProductModel.isOpen">
    <v-card title="Add New Product">
      <v-card-text>
        <v-select
          v-model="addProductModel.data.teamLevel"
          label="Team Level"
          :items="avaliableTeamLevels"
          return-object
          item-title="team_level"
          :rules="[rules.required]"
          outlined></v-select>
        <!-- Product Variants Section -->
        <ProductPanelComponent
          type="create"
          :product="addProductModel.data"
          :yearId="yearId"
          @productCreateSelect="addProductModel.isOpen = false">
        </ProductPanelComponent>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="editProductModel.isOpen">
    <v-card title="Edit Product">
      <v-card-text>
        <!-- Product Variants Section -->
        <ProductPanelComponent
          :product="selectedProduct"
          type="edit"
          :year-id="yearId"
          @productEditSelect="handleProductEdit">
        </ProductPanelComponent>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { IProductDTO, IYearDTO, ProductDTO } from '@/dtos';
import { rules } from '@/helpers';
import { computed, ref, watch } from 'vue';
import ProductPanelComponent from '@/components/products/ProductPanelComponent.vue';
import ConfirmDeleteComponent from '@/components/ui/ConfirmDeleteComponent.vue';
import { useProductStore } from '@/stores/modules/admin';
import { SNACKSTATUS } from '@/stores/util';
import { useUpdateStatusStore } from '@/stores/modules';

interface Props {
  year: IYearDTO;
}
const props = defineProps<Props>();
const emit = defineEmits(['snack']);
const yearId = computed(() => {
  return props.year.id;
});
const productStore = useProductStore();
const statusStore = useUpdateStatusStore();
const productsData = productStore.productsForYearQuery(yearId);
const products = computed(() => productsData.data.value);
const availableQuery = productStore.availableProductTeamLevelsQuery(yearId);
const avaliableTeamLevels = computed(() => availableQuery.data.value);
const addProductModel = ref<{ isOpen: boolean; data: IProductDTO }>({
  isOpen: false,
  data: new ProductDTO(),
});

const editProductModel = ref({
  isOpen: false,
});

const isRemoveModalOpen = ref<boolean>(false);

const selectedProduct = ref<IProductDTO>(new ProductDTO());

watch(
  () => statusStore.getStatus(`product-${selectedProduct.value.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `product-${selectedProduct.value.id}`;
    emit('snack', 'Product', value, statusStore.getError(key));
  },
);

watch(
  () => statusStore.getStatus(`product-create`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `product-create`;
    emit('snack', 'Product', value, statusStore.getError(key));
  },
);

const openModal = (type: string) => {
  if (type === 'productAdd') {
    addProductModel.value.isOpen = true;
  }
};

const handleProductEdit = async () => {
  editProductModel.value.isOpen = false;
  selectedProduct.value = new ProductDTO();
};

const selectProduct = async (product: IProductDTO) => {
  selectedProduct.value = new ProductDTO(product);
  editProductModel.value.isOpen = true;
};

const removeProductModal = async (product: IProductDTO) => {
  selectedProduct.value = new ProductDTO(product);
  isRemoveModalOpen.value = true;
};

const handleRemoveProduct = async () => {
  productStore.remove.mutateAsync({ productId: selectedProduct.value.id, yearId: yearId.value });
  isRemoveModalOpen.value = false;
};
</script>
